(function () {
  const WIDGET_SELECTOR = '.delivery-estimate-widget';
  const INITIALISED_ATTR = 'data-delivery-widget-ready';

  async function getBankHolidays(region) {
    try {
      const res = await fetch('https://www.gov.uk/bank-holidays.json');
      if (!res.ok) throw new Error('Bank holiday fetch failed');
      const data = await res.json();
      const events = data[region] && data[region].events ? data[region].events : [];
      return new Set(events.map(event => event.date));
    } catch (error) {
      console.warn('Could not load bank holidays', error);
      return new Set();
    }
  }

  function toISODateLondon(date) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);

    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;

    return `${year}-${month}-${day}`;
  }

  function getLondonParts(date) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);

    const get = (type) => parts.find(p => p.type === type)?.value || '';

    return {
      weekday: get('weekday'),
      year: parseInt(get('year'), 10),
      monthName: get('month'),
      day: parseInt(get('day'), 10),
      hour: parseInt(get('hour'), 10),
      minute: parseInt(get('minute'), 10)
    };
  }

  function ordinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  function formatDateLabel(date) {
    const p = getLondonParts(date);
    return `${p.weekday}, ${p.day}${ordinalSuffix(p.day)} ${p.monthName}`;
  }

  function isWeekendLondon(date) {
    const weekday = getLondonParts(date).weekday;
    return weekday === 'Saturday' || weekday === 'Sunday';
  }

  function addDays(date, days) {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() + days);
    return d;
  }

  function isWorkingDay(date, bankHolidaySet) {
    return !isWeekendLondon(date) && !bankHolidaySet.has(toISODateLondon(date));
  }

  function nextWorkingDay(date, bankHolidaySet) {
    let d = addDays(date, 1);
    while (!isWorkingDay(d, bankHolidaySet)) {
      d = addDays(d, 1);
    }
    return d;
  }

  function getDispatchDate(now, cutoffHour, cutoffMinute, bankHolidaySet) {
    const london = getLondonParts(now);

    const beforeCutoff =
      isWorkingDay(now, bankHolidaySet) &&
      (london.hour < cutoffHour ||
        (london.hour === cutoffHour && london.minute < cutoffMinute));

    if (beforeCutoff) return now;

    let d = addDays(now, 1);
    while (!isWorkingDay(d, bankHolidaySet)) {
      d = addDays(d, 1);
    }
    return d;
  }

  function getCountdown(now, cutoffHour, cutoffMinute, bankHolidaySet) {
    if (!isWorkingDay(now, bankHolidaySet)) return null;

    const london = getLondonParts(now);
    const minutesNow = london.hour * 60 + london.minute;
    const cutoffTotal = cutoffHour * 60 + cutoffMinute;

    if (minutesNow >= cutoffTotal) return null;

    const diff = cutoffTotal - minutesNow;
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }
    if (hours > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  function isThrottleVariant(variant) {
    if (!variant || !variant.title) return false;
    return variant.title.toLowerCase().includes('throttle dvsa certified');
  }

  function getProductFormForWidget(widget) {
    return widget.closest('product-info, product-rerender, section, .shopify-section')?.querySelector('form[action*="/cart/add"]')
      || document.querySelector('form[action*="/cart/add"]');
  }

  function getCurrentVariant(widget, variants) {
    const form = getProductFormForWidget(widget);
    const fallbackId = widget.dataset.selectedVariantId;

    if (!form) {
      return variants.find(v => String(v.id) === String(fallbackId)) || variants[0];
    }

    const variantInput = form.querySelector('input[name="id"], select[name="id"]');
    const variantId = variantInput ? variantInput.value : fallbackId;

    return variants.find(v => String(v.id) === String(variantId)) || variants[0];
  }

  function renderWidget(widget, bankHolidaySet, variants) {
    const cutoffHour = parseInt(widget.dataset.cutoffHour || '14', 10);
    const cutoffMinute = parseInt(widget.dataset.cutoffMinute || '0', 10);

    const beforeTemplate = widget.dataset.deliveryLabelBefore || '🚚 Get it by {date}';
    const afterTemplate = widget.dataset.deliveryLabelAfter || '📦 Expected delivery by {date}';
    const countdownPrefix = widget.dataset.countdownPrefix || '⏰ Order within ';
    const afterCutoffText = widget.dataset.afterCutoffText || 'Next business day dispatch';

    const throttleLine1 = widget.dataset.throttleLine1 || '🛠️ Throttle DVSA certification required';
    const throttleLine2 = widget.dataset.throttleLine2 || 'Usual lead time: 2–5 weeks';

    const now = new Date();
    const currentVariant = getCurrentVariant(widget, variants);



    const dispatchDate = getDispatchDate(now, cutoffHour, cutoffMinute, bankHolidaySet);
    const deliveryDate = nextWorkingDay(dispatchDate, bankHolidaySet);
    const countdown = getCountdown(now, cutoffHour, cutoffMinute, bankHolidaySet);

const line1 = countdown
  ? beforeTemplate.replace('{date}', formatDateLabel(deliveryDate))
  : afterTemplate.replace('{date}', formatDateLabel(deliveryDate));

const line2 = countdown
  ? `${countdownPrefix}${countdown}`
  : afterCutoffText;

const extraNote = isThrottleVariant(currentVariant)
  ? `<div class="delivery-estimate-box__note">${throttleLine2}</div>`
  : '';

const pedalExplainerTitle = widget.dataset.pedalExplainerTitle || 'Options Explained:';
const pedalExplainerText = widget.dataset.pedalExplainerText || '';
const throttleExplainerTitle = widget.dataset.throttleExplainerTitle || 'Options Explained:';
const throttleExplainerText = widget.dataset.throttleExplainerText || '';

const explainerTitle = isThrottleVariant(currentVariant) ? throttleExplainerTitle : pedalExplainerTitle;
const explainerText = isThrottleVariant(currentVariant) ? throttleExplainerText : pedalExplainerText;

const explainer = explainerText
  ? `
    <div class="delivery-estimate-box__explainer">
      <div class="delivery-estimate-box__explainer-title">${explainerTitle}</div>
      <div class="delivery-estimate-box__explainer-text">${explainerText}</div>
    </div>
  `
  : '';

widget.innerHTML = `
  <div class="delivery-estimate-box__inner">
    <div class="delivery-estimate-box__line1">${line1}</div>
    <div class="delivery-estimate-box__line2">${line2}</div>
    ${extraNote}
    ${explainer}
  </div>
`;

  }

  async function initWidget(widget) {
    if (!widget || widget.hasAttribute(INITIALISED_ATTR)) return;
    widget.setAttribute(INITIALISED_ATTR, 'true');

    let variants = [];
    try {
      variants = JSON.parse(widget.dataset.variants || '[]');
    } catch (e) {
      console.warn('Could not parse variants JSON', e);
    }

    const region = widget.dataset.region || 'england-and-wales';
    const bankHolidaySet = await getBankHolidays(region);

    const render = () => renderWidget(widget, bankHolidaySet, variants);
    render();

    const timer = setInterval(() => {
      if (!document.body.contains(widget)) {
        clearInterval(timer);
        return;
      }
      render();
    }, 60000);

    const form = getProductFormForWidget(widget);
    if (form) {
      let lastVariantId =
        form.querySelector('input[name="id"], select[name="id"]')?.value || '';

      const watchVariantInput = () => {
        const currentId =
          form.querySelector('input[name="id"], select[name="id"]')?.value || '';
        if (currentId !== lastVariantId) {
          lastVariantId = currentId;
          render();
        }
      };

      form.addEventListener('change', () => {
        setTimeout(watchVariantInput, 50);
      });

      form.addEventListener('input', () => {
        setTimeout(watchVariantInput, 50);
      });

      const variantInput = form.querySelector('input[name="id"], select[name="id"]');
      if (variantInput) {
        const inputObserver = new MutationObserver(() => {
          watchVariantInput();
        });

        inputObserver.observe(variantInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }
    }
  }

  function initAllWidgets() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(initWidget);
  }

  document.addEventListener('DOMContentLoaded', initAllWidgets);
  document.addEventListener('shopify:section:load', initAllWidgets);

  const pageObserver = new MutationObserver(() => {
    initAllWidgets();
  });

  pageObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
})();